/**
 * Payroll Review API client (Phase 1).
 */
import { fetchApi } from './api';

export interface PayrollEntry {
    Id: number;
    CompanyCode: string;
    YearMonth: number;
    EmployeeEmail: string;
    /** Canonical name from etl.EmployeeMaster.FullName (empty string if no match). */
    EmployeeFullName: string;
    EmployeeNameRaw: string;
    PersonalNumber: string | null;
    /** Base salary — excludes Bonus and CarPrivateShare. */
    GrossSalary: number;
    Bonus: number | null;
    CarPrivateShare: number | null;
    /** Total as printed on the payslip = GrossSalary + Bonus + CarPrivateShare. */
    GrossReported: number | null;
    SocialEmployer: number | null;
    NetSalary: number | null;
    Currency: string;
    SourceFileId: number;
    SourceFileName: string | null;
    SourceWebUrl: string | null;
    ConfidenceScore: number | null;
    Flags: string;
    ExtractedAt: string;
}

export interface PayrollStatRow {
    CompanyCode: string;
    Currency: string;
    RecordCount: number;
    EmployeeCount: number;
    TotalGross: number;
    TotalBonus: number;
    TotalCarPrivateShare: number;
    TotalGrossReported: number;
    TotalSocialEmployer: number | null;
    TotalNet: number | null;
    MatchedEmail: number;
    FlaggedRows: number;
}

export interface PayrollEntriesResponse {
    entries: PayrollEntry[];
    stats: PayrollStatRow[];
    total: number;
}

export interface PayrollFile {
    Id: number;
    CountryFolder: string;
    CompanyCode: string;
    FileName: string;
    Status: string;
    RowsExtracted: number;
    DurationMs: number | null;
    ExtractorModel: string | null;
    ErrorMessage: string | null;
    IngestedAt: string;
    FileLastModified: string | null;
    WebUrl: string | null;
}

export interface PayrollFilesSummaryRow {
    CompanyCode: string;
    Total: number;
    Success: number;
    Failed: number;
    Unparseable: number;
    Skipped: number;
    LastIngest: string;
}

export interface PayrollFilesResponse {
    files: PayrollFile[];
    summary: PayrollFilesSummaryRow[];
}

function buildQuery(params: Record<string, string | number | null | undefined>): string {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === null || v === undefined || v === '') continue;
        usp.append(k, String(v));
    }
    const qs = usp.toString();
    return qs ? `?${qs}` : '';
}

// fetchApi already unwraps body.data — no second .data hop needed here.
export async function listPayrollEntries(filters: {
    company?: string | null;
    year?: number | null;
    month?: number | null;
    search?: string | null;
}): Promise<PayrollEntriesResponse> {
    return fetchApi<PayrollEntriesResponse>(`/payroll/entries${buildQuery(filters)}`);
}

export async function listPayrollFiles(filters: { company?: string | null }): Promise<PayrollFilesResponse> {
    return fetchApi<PayrollFilesResponse>(`/payroll/files${buildQuery(filters)}`);
}
